import React, { useState, useEffect } from 'react';
import { Sparkles, Search, Zap, Database, BarChart3, ArrowRight, X, Check, Newspaper, Rss, Bell, Users, MessageSquare, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';

// iter196 — Tour version key. Bumped whenever new steps are added so
// existing users who already dismissed the tour see the new content
// exactly once. localStorage key is versioned; DashboardLayout uses
// this to decide whether to re-run the tour even for users whose
// server-side `has_seen_onboarding` is true.
export const TOUR_VERSION = 2;
export const TOUR_VERSION_STORAGE_KEY = 'dotbox_onboarding_tour_version';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to dotbox!',
    description: 'Your AI-powered data aggregation platform. Let us show you around in under a minute — including a few things you may not have seen yet.',
    icon: Sparkles,
    position: 'center',
  },
  {
    id: 'ask-ai',
    title: 'Ask AI',
    description: 'Use natural language to search and analyze content from your data sources. Try something like "Find AI trends in healthcare".',
    icon: Search,
    position: 'center',
    highlight: '[data-testid="nlp-input"]',
  },
  {
    id: 'connected-sources',
    title: 'Your Live Content Stream',
    description: 'The default view — one card per connected source, showing the latest articles. AI summaries, filters, and bookmarks all start here.',
    icon: Newspaper,
    position: 'center',
    highlight: '[data-testid="connected-sources-tab"]',
  },
  {
    id: 'data-sources',
    title: 'Add Your Own Sources',
    description: 'The Data Sources sub-tab lets you plug in any RSS feed, URL, or API — your signals and AI queries search across every one you add.',
    icon: Database,
    position: 'center',
    highlight: '[data-testid="data-sources-tab"]',
  },
  {
    id: 'feeds',
    title: 'Or Pick from Curated Feeds',
    description: 'Not sure where to start? The Feeds sub-tab has 120+ credible RSS sources across 30 industries — add any to your stream in one click.',
    icon: Rss,
    position: 'center',
    highlight: '[data-testid="feeds-tab"]',
  },
  {
    id: 'signals',
    title: 'Create Signals',
    description: 'Signals are AI-powered monitors that automatically find and filter content matching your interests. Schedule them to run automatically.',
    icon: Zap,
    position: 'center',
    highlight: '[data-testid="signals-tab"]',
  },
  // iter196 — New tour steps for the sentiment / team / recap features
  // shipped across iter188 → iter195. Existing users see them once via
  // the TOUR_VERSION bump; new users see the whole flow first-run.
  {
    id: 'sentiment-alerts',
    title: 'Sentiment Alerts',
    description: 'dotbox now watches your topics 24/7 and pings you the moment sentiment turns negative or article volume spikes. Set thresholds per watch — no more manual scanning.',
    icon: Bell,
    position: 'center',
  },
  {
    id: 'team-watches',
    title: 'Shared Team Watches',
    description: 'Share a watch with your workspace and every teammate gets the same alerts. Anyone can tune, mute, or acknowledge — and each teammate can opt out of just the noisy ones.',
    icon: Users,
    position: 'center',
  },
  {
    id: 'ack-notes',
    title: 'Ack Notes & 👍/👎 Reactions',
    description: 'When a teammate acknowledges a noisy streak, they can leave a note explaining why. React with 👍 (agree) or 👎 (disagree) so review consensus builds over time.',
    icon: MessageSquare,
    position: 'center',
  },
  {
    id: 'activity-feed',
    title: 'Team Activity Feed & Recap',
    description: 'The new Activity tab on Sentiment Alerts shows who tuned, muted, and acked what across your workspace. Export a weekly recap as CSV or email it to the team from the same page.',
    icon: Activity,
    position: 'center',
  },
  {
    id: 'analytics',
    title: 'Track Everything',
    description: 'View content trends, signal performance, and source breakdowns in the Analytics dashboard. Share, export, and organize with folders and lists.',
    icon: BarChart3,
    position: 'center',
  },
  {
    id: 'done',
    title: "You're all set!",
    description: 'Start by browsing Feeds or adding a Data Source, then use Ask AI or create a Signal. Need help? Check Settings for more options.',
    icon: Check,
    position: 'center',
  },
];

const OnboardingTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(true);

  const step = TOUR_STEPS[currentStep];
  const isLast = currentStep === TOUR_STEPS.length - 1;
  const Icon = step.icon;

  useEffect(() => {
    // Highlight element if specified
    if (step.highlight) {
      const el = document.querySelector(step.highlight);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.position = 'relative';
        el.style.zIndex = '60';
        el.style.boxShadow = '0 0 0 4px rgba(74, 144, 217, 0.4), 0 0 20px rgba(74, 144, 217, 0.2)';
        el.style.borderRadius = '8px';
        el.style.transition = 'box-shadow 0.3s ease';
      }
      return () => {
        if (el) {
          el.style.zIndex = '';
          el.style.boxShadow = '';
          el.style.borderRadius = '';
        }
      };
    }
  }, [currentStep, step.highlight]);

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setVisible(false);
    // iter196 — Persist the tour version we just completed so future
    // version bumps re-show the tour without losing the "has seen" flag.
    try {
      localStorage.setItem(TOUR_VERSION_STORAGE_KEY, String(TOUR_VERSION));
    } catch {
      // localStorage unavailable (private mode) — fall through; server
      // flag will still gate the re-show.
    }
    onComplete?.();
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
        onClick={handleSkip}
        data-testid="onboarding-tour-backdrop"
      />

      {/* Tour Card */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
          onClick={e => e.stopPropagation()}
          data-testid="onboarding-tour-card"
        >
          {/* Skip button */}
          <button 
            onClick={handleSkip}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-300"
            data-testid="onboarding-tour-close"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className={`p-4 rounded-full ${isLast ? 'bg-emerald-900/30' : 'bg-[#DC2626]/10'}`}>
              <Icon className={`h-8 w-8 ${isLast ? 'text-emerald-400' : 'text-red-400'}`} />
            </div>
          </div>

          {/* Content */}
          <h2 className="font-bold text-white mb-2" data-testid={`onboarding-tour-title-${step.id}`}>
            {step.title}
          </h2>
          <p className="text-gray-400 mb-6 leading-relaxed" data-testid={`onboarding-tour-desc-${step.id}`}>
            {step.description}
          </p>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-5">
            {TOUR_STEPS.map((s, idx) => (
              <div
                key={s.id}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentStep ? 'w-6 bg-[#DC2626]' : 
                  idx < currentStep ? 'w-1.5 bg-[#DC2626]/50' : 'w-1.5 bg-gray-600'
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="hover:text-slate-200 text-gray-400"
              data-testid="onboarding-tour-skip"
            >
              Skip tour
            </Button>
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  data-testid="onboarding-tour-back"
                >
                  Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className={isLast ? 'bg-green-600 hover:bg-green-700' : 'bg-[#DC2626] hover:bg-[#B91C1C]'}
                data-testid="onboarding-tour-next"
              >
                {isLast ? (
                  <><Check className="mr-1 h-4 w-4" /> Get Started</>
                ) : (
                  <>Next <ArrowRight className="ml-1 h-4 w-4" /></>
                )}
              </Button>
            </div>
          </div>

          {/* Step counter */}
          <p className="text-center text-xs text-gray-400 mt-3">
            Step {currentStep + 1} of {TOUR_STEPS.length}
          </p>
        </div>
      </div>
    </>
  );
};

export default OnboardingTour;
